import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from '../../../../components/Table';
import Button from '../../../../components/Button';
import Pagination from '../../../../components/Pagination';
import { GenericDictFilters, DrugInfoSection } from '../components';
import Navbar from '../../../../components/navbar';
import Footer from '../../../../components/footer';
import styles from '../css/GenericDictionary.module.css';
import popupStyles from '../css/GenericDictionaryPopup.module.css';

const GenericDictionary = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    activeIngredient: '',
    manufacturer: '',
    brand: ''
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Sample data - this would typically come from an API
  const [drugsData] = useState([
    {
      nappi: '3001013001',
      tradeName: 'Auro Altacef/ Lamivudine',
      supplier: 'Aurobindo Pharma',
      schedule: '54',
      presentation: 'TABS',
      strength: '30',
      packSize: 'R836.14',
      sepIncl: 'R564.57',
      mplIncl: 'R595.00',
      mmapIncl: 'R564.78',
      mrpIncl: 'R564.78',
      drugInfo: {
        indications: 'Treatment of infections caused by sensitive organisms, of the urinary, gastrointestinal and lower respiratory tract, skin and soft tissue. It is the drug of choice for typhoid fever.',
        pharmacokinetics: 'Oral bioavailability: 70-80%. Widely distributed with good penetration of tissues and bone. T½ 6-8 hours, slightly longer in renal failure. It is metabolised in the liver.',
        adultDose: 'Over 18 years: Oral: 250-750 mg twice daily, depending on the nature and the severity of infection in patients living with HIV. In infusion over 60 minutes: 200-400 mg 8-12 hourly.',
        paediatricDose: 'Neonates: oral: 15 mg/kg dose 12 hourly in the first 4 weeks of life, then 8 hourly. Over 1 month old: oral: 15-25mg/kg dose (max: 300 mg) 12 hourly.',
        drugInteractions: 'Ciprofloxacin is an inhibitor of hepatic microsomal enzymes, therefore clearance of theophylline is reduced, leading to increased serum levels.',
        precautions: 'Ciprofloxacin Silver Pharma tablets 250 mg, 500 mg, 750 mg: 98 tablets 500 mg, 1.6 suspension 250 mg/5 mL in 100 mL saline. Aurobindo Ciprofloxacin; Biotech Ciprofloxacin; Cifloxin Or Sandoz; Ciproflox Cipla Medpro; Ciprofloxacin Actavis; Ciprofloxacin Teva tablets: 250 mg, 500 mg.',
        cautions: 'Paediatrics: Studies have shown damage to cartilage of weight-bearing joints in immature animals. Therefore only use in patients under 18 years old for severe infections when there are no other alternatives.'
      }
    },
    {
      nappi: '722096001',
      tradeName: 'Cipla Altacef & Lamivudine',
      supplier: 'Cipla Medpro',
      schedule: '54',
      presentation: 'TABS',
      strength: '30',
      packSize: 'R836.14',
      sepIncl: 'R564.57',
      mplIncl: 'R595.00',
      mmapIncl: 'R564.78',
      mrpIncl: 'R564.78',
      drugInfo: {
        indications: 'Treatment of infections caused by sensitive organisms, of the urinary, gastrointestinal and lower respiratory tract, skin and soft tissue.',
        pharmacokinetics: 'Oral bioavailability: 70-80%. Widely distributed with good penetration of tissues and bone.',
        adultDose: 'Over 18 years: Oral: 250-750 mg twice daily, depending on the nature and severity of infection.',
        paediatricDose: 'Neonates: oral: 15 mg/kg dose 12 hourly in the first 4 weeks of life.',
        drugInteractions: 'May interact with theophylline and other medications metabolized by hepatic enzymes.',
        precautions: 'Use with caution in patients with renal impairment.',
        cautions: 'Monitor liver function regularly during treatment. Discontinue if signs of hepatotoxicity occur.'
      }
    },
    {
      nappi: '717977001',
      tradeName: 'Duriva',
      supplier: 'Mylan Pharma',
      schedule: '54',
      presentation: 'TABS',
      strength: '30',
      packSize: 'R594.56',
      sepIncl: 'R564.57',
      mplIncl: 'R595.00',
      mmapIncl: 'R564.78',
      mrpIncl: 'R564.78',
      drugInfo: {
        indications: 'Antiretroviral medication used in combination therapy for HIV treatment.',
        pharmacokinetics: 'Well absorbed orally with high bioavailability.',
        adultDose: 'One tablet daily as part of combination therapy.',
        paediatricDose: 'Dosing based on weight and age, consult prescribing information.',
        drugInteractions: 'Multiple drug interactions possible with other antiretrovirals.',
        precautions: 'Monitor for hepatotoxicity and lactic acidosis.',
        cautions: 'Regular monitoring of viral load and CD4 count required. Discontinue if severe side effects occur.'
      }
    },
    {
      nappi: '3003823001',
      tradeName: 'Hetbacin',
      supplier: 'Activo Health',
      schedule: '54',
      presentation: 'TABS',
      strength: '30',
      packSize: 'R730.25',
      sepIncl: 'R564.57',
      mplIncl: 'R595.00',
      mmapIncl: 'R564.78',
      mrpIncl: 'R564.78',
      drugInfo: {
        indications: 'Broad-spectrum antibiotic for bacterial infections.',
        pharmacokinetics: 'Good oral absorption and tissue penetration.',
        adultDose: 'Varies based on indication and severity of infection.',
        paediatricDose: 'Weight-based dosing recommended.',
        drugInteractions: 'May interact with anticoagulants and other antibiotics.',
        precautions: 'Monitor renal function during treatment.',
        cautions: 'Avoid use in patients with known hypersensitivity to beta-lactam antibiotics. Monitor for allergic reactions.'
      }
    },
    {
      nappi: '724672002',
      tradeName: 'Hetracin 600/300',
      supplier: 'Hetero Drugs',
      schedule: '54',
      presentation: 'TABS',
      strength: '28',
      packSize: 'R498.32',
      sepIncl: 'R565.93',
      mplIncl: 'R535.24',
      mmapIncl: 'R535.14',
      mrpIncl: 'R535.14',
      drugInfo: {
        indications: 'Combination antiretroviral therapy for HIV treatment.',
        pharmacokinetics: 'Dual drug combination with complementary pharmacokinetic profiles.',
        adultDose: 'One tablet twice daily with food.',
        paediatricDose: 'Not recommended for pediatric use.',
        drugInteractions: 'Extensive interactions with other medications.',
        precautions: 'Regular monitoring of liver function required.',
        cautions: 'Not recommended for use in pregnancy. Ensure adequate contraception during treatment.'
      }
    }
  ]);

  const itemsPerPage = 5;
  const totalPages = Math.ceil(drugsData.length / itemsPerPage);

  // Filter and paginate data
  const filteredData = drugsData.filter(drug => {
    const matchesSearch = searchTerm === '' || 
      drug.tradeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      drug.supplier.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilters = 
      (filters.activeIngredient === '' || drug.tradeName.toLowerCase().includes(filters.activeIngredient.toLowerCase())) &&
      (filters.manufacturer === '' || drug.supplier.toLowerCase().includes(filters.manufacturer.toLowerCase())) &&
      (filters.brand === '' || drug.tradeName.toLowerCase().includes(filters.brand.toLowerCase()));
    
    return matchesSearch && matchesFilters;
  });

  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleViewDrug = (drug) => {
    setSelectedDrug(drug);
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedDrug(null);
  };

  const handleEditDrug = () => {
    // Implementation for edit functionality
    console.log('Edit drug:', selectedDrug);
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const columns = [
    { header: 'NAPPI', accessor: 'nappi' },
    { header: 'Trade Name', accessor: 'tradeName' },
    { header: 'Supplier', accessor: 'supplier' },
    { header: 'Schedule', accessor: 'schedule' },
    { header: 'Presentation', accessor: 'presentation' },
    { header: 'Strength', accessor: 'strength' },
    { header: 'Pack Size', accessor: 'packSize' },
    { header: 'SEP Incl.', accessor: 'sepIncl' },
    { header: 'MPL Incl.', accessor: 'mplIncl' },
    { header: 'MMAP Incl.', accessor: 'mmapIncl' },
    { header: 'MRP Incl.', accessor: 'mrpIncl' },
    {
      header: 'Action',
      accessor: 'action',
      render: (value, row) => (
        <Button
          onClick={() => handleViewDrug(row)}
          variant="primary"
          className={styles.viewButton}
        >
          View
        </Button>
      )
    }
  ];

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      <main className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Generics Dictionary</h1>
        </div>

        <GenericDictFilters
          onSearch={handleSearch}
          onFilterChange={handleFilterChange}
          filters={filters}
        />

        <div className={styles.tableContainer}>
          <Table columns={columns} data={paginatedData} />
        </div>

        <div className={styles.paginationContainer}>
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(filteredData.length / itemsPerPage)}
            onPageChange={setCurrentPage}
          />
        </div>

        {/* Popup Modal */}
        {showPopup && selectedDrug && (
          <div className={popupStyles.overlay}>
            <div className={popupStyles.popup}>
              <div className={popupStyles.header}>
                <h2 className={popupStyles.title}>{selectedDrug.tradeName}</h2>
                <button
                  className={popupStyles.closeButton}
                  onClick={handleClosePopup}
                >
                  ×
                </button>
              </div>
              
              <div className={popupStyles.content}>
                <div className={popupStyles.columnsContainer}>
                  <div className={popupStyles.leftColumn}>
                    <DrugInfoSection
                      title="Indications"
                      content={selectedDrug.drugInfo.indications}
                    />
                    <DrugInfoSection
                      title="Adult dose"
                      content={selectedDrug.drugInfo.adultDose}
                    />
                    <DrugInfoSection
                      title="Cautions"
                      content={selectedDrug.drugInfo.cautions}
                    />
                  </div>
                  <div className={popupStyles.rightColumn}>
                    <DrugInfoSection
                      title="Pharmacokinetics"
                      content={selectedDrug.drugInfo.pharmacokinetics}
                    />
                    <DrugInfoSection
                      title="Drug interactions"
                      content={selectedDrug.drugInfo.drugInteractions}
                    />
                    <DrugInfoSection
                      title="Paediatric dose"
                      content={selectedDrug.drugInfo.paediatricDose}
                    />
                  </div>
                </div>
                <div className={popupStyles.fullWidthSection}>
                  <DrugInfoSection
                    title="Precautions"
                    content={selectedDrug.drugInfo.precautions}
                  />
                </div>
              </div>

              <div className={popupStyles.footer}>
                <Button
                  onClick={handleEditDrug}
                  variant="primary"
                  className={popupStyles.editButton}
                >
                  Edit
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default GenericDictionary;
